// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * CSV reading — RFC 4180 — shared by the frontend and the backend.
 *
 * fix-sprint-20260506-2121-followup L4. The DSR batch page carried its own
 * inline parser, untested on its own, beside the backend's line-based
 * `parseCSVLine` (`backend/src/utils/csv-parser.ts`). That backend parser now
 * tokenises through `splitCsvLine` here, and the page through `parseCsvGrid`,
 * so the two read a field the same way.
 *
 * Reading only: nothing here sanitises a value. Formula-injection stripping is
 * an import concern and stays in the backend's `sanitizeCellValue`.
 */
/**
 * Split ONE physical line into fields: commas separate, a field may be quoted,
 * `""` inside quotes is one quote. An empty line is one empty field.
 */
export function splitCsvLine(line) {
    const fields = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i += 1) {
        const ch = line[i];
        if (inQuotes) {
            if (ch === '"') {
                if (line[i + 1] === '"') {
                    current += '"';
                    i += 1;
                }
                else {
                    inQuotes = false;
                }
            }
            else {
                current += ch;
            }
        }
        else if (ch === '"') {
            inQuotes = true;
        }
        else if (ch === ',') {
            fields.push(current);
            current = '';
        }
        else {
            current += ch;
        }
    }
    fields.push(current);
    return fields;
}
/**
 * Parse a whole CSV document into rows of fields. A quoted field may span line
 * breaks; LF, CRLF and CR all end a row; a leading byte-order mark is dropped;
 * rows whose every field is blank are skipped.
 */
export function parseCsvGrid(text) {
    const source = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
    const rows = [];
    let row = [];
    let cell = '';
    let inQuotes = false;
    const endRow = () => {
        row.push(cell);
        cell = '';
        if (row.some((c) => c.trim() !== ''))
            rows.push(row);
        row = [];
    };
    for (let i = 0; i < source.length; i += 1) {
        const ch = source[i];
        if (inQuotes) {
            if (ch === '"') {
                if (source[i + 1] === '"') {
                    cell += '"';
                    i += 1;
                }
                else {
                    inQuotes = false;
                }
            }
            else {
                cell += ch;
            }
            continue;
        }
        if (ch === '"') {
            inQuotes = true;
        }
        else if (ch === ',') {
            row.push(cell);
            cell = '';
        }
        else if (ch === '\n' || ch === '\r') {
            if (ch === '\r' && source[i + 1] === '\n')
                i += 1;
            endRow();
        }
        else {
            cell += ch;
        }
    }
    if (cell.length > 0 || row.length > 0)
        endRow();
    return rows;
}
/**
 * A CSV document as a lowercase, trimmed header and its rows. `get(row, name)`
 * reads one trimmed cell by header name ('' when the column is absent).
 */
export function parseCsvRecords(text) {
    const [first, ...rows] = parseCsvGrid(text);
    const header = (first ?? []).map((h) => h.trim().toLowerCase());
    const get = (row, name) => {
        const index = header.indexOf(name.toLowerCase());
        return index >= 0 ? (row[index] ?? '').trim() : '';
    };
    return { header, rows, get };
}
//# sourceMappingURL=csv.js.map